import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs';
import { ApiControllers } from '../constants/api-controllers.constants';
import { EntityApiService } from './entity-api.service';
import { StateDropdownDto, StateService } from './state.service';
import {
  createPagedListRequest,
  normalizePagedList,
} from '../utils/paged-list.util';
import { ItemDto } from '../models/dtos';
import { DropdownOption } from '../../shared/custom-select/custom-select.component';

export type MasterDataCacheKey =
  | 'suppliers'
  | 'items'
  | 'raw-items'
  | 'purities'
  | 'categories'
  | 'states';

/** Session TTL for master dropdown/list caches (ms). */
const CACHE_TTL_MS = 10 * 60 * 1000;

interface CacheEntry<T> {
  expiresAt: number;
  value: T;
}

@Injectable({ providedIn: 'root' })
export class MasterDataCacheService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly entityApi = inject(EntityApiService);
  private readonly stateService = inject(StateService);

  private readonly memory = new Map<MasterDataCacheKey, CacheEntry<unknown>>();
  private readonly inflight = new Map<MasterDataCacheKey, Observable<unknown>>();

  private readonly itemApi = this.entityApi.crud<ItemDto>(ApiControllers.Item);

  /** Clear all cached master data (call on logout). */
  clearAll(): void {
    this.memory.clear();
    this.inflight.clear();
  }

  invalidate(key: MasterDataCacheKey): void {
    this.memory.delete(key);
    this.inflight.delete(key);
  }

  /**
   * Background warmup for frequent dropdowns — safe to call after layout loads.
   * Does not block UI; failures are ignored.
   */
  warmupFrequent(): void {
    // Accounting-only app — no jewellery master warmup.
  }

  getItemOptions(): Observable<DropdownOption[]> {
    return this.getItems().pipe(
      map((rows) =>
        rows.map((i) => ({
          value: i.itemId!,
          label: `${i.itemCode ?? ''} - ${i.item}`.replace(/^ - /, ''),
        })),
      ),
    );
  }

  getItems(): Observable<ItemDto[]> {
    return this.getRaw<ItemDto[]>('items');
  }

  getStates(): Observable<StateDropdownDto[]> {
    return this.getRaw<StateDropdownDto[]>('states');
  }

  private getRaw<T>(key: MasterDataCacheKey): Observable<T> {
    if (!isPlatformBrowser(this.platformId)) {
      return of([] as T);
    }

    const hit = this.memory.get(key);
    if (hit && hit.expiresAt > Date.now()) {
      return of(hit.value as T);
    }

    const pending = this.inflight.get(key);
    if (pending) {
      return pending as Observable<T>;
    }

    const request$ = this.fetch(key).pipe(
      map((value) => value as T),
      tap((value) => {
        this.memory.set(key, {
          value,
          expiresAt: Date.now() + CACHE_TTL_MS,
        });
        this.inflight.delete(key);
      }),
      catchError((err) => {
        this.inflight.delete(key);
        return throwError(() => err);
      }),
      shareReplay(1),
    );

    this.inflight.set(key, request$ as Observable<unknown>);
    return request$;
  }

  private fetch(key: MasterDataCacheKey): Observable<unknown> {
    switch (key) {
      case 'items':
        return this.itemApi
          .getList(this.allRowsRequest('Item'))
          .pipe(map((res) => normalizePagedList(res.data).items));
      case 'raw-items':
        return this.itemApi
          .getList({
            ...this.allRowsRequest('Item'),
            rawItemOnly: true,
          } as Parameters<typeof this.itemApi.getList>[0])
          .pipe(map((res) => normalizePagedList(res.data).items));
      case 'states':
        return this.stateService
          .getDropdown()
          .pipe(map((res) => (res.success && res.data ? res.data : [])));
      default:
        return of([]);
    }
  }

  private allRowsRequest(sortColumn: string) {
    return createPagedListRequest({
      pageNumber: 1,
      pageSize: -1,
      sortColumn,
      sortDirection: 'ASC',
    });
  }
}
